// ============================================================================
// DayTimelineView — Iter 62fe
// Vue "journée horaire" type Apple Calendar. Grille verticale 0h → 24h, les
// tâches sont positionnées absolument selon heure_debut/heure_fin.
// Réservé aux founders (Rayan + Hugo).
// ============================================================================
import { useMemo, useRef, useEffect, useState } from 'react';
import { JoinCallButton } from '../components/JoinCallButton';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { ChevronLeft, ChevronRight, Plus, LayoutGrid } from 'lucide-react';
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable, DragOverlay } from '@dnd-kit/core';

const NAVY = '#0B1330';
const BLUE = '#3E7BFA';
const BLUE_LIGHT = '#5BBEFC';
const SURFACE = 'rgba(20, 29, 66, 0.62)';
const SURFACE2 = 'rgba(28, 38, 80, 0.55)';
const BORDER = 'rgba(50, 64, 120, 0.5)';
const T1 = '#F0F4FF';
const T2 = '#C7D1E8';
const T3 = '#8A96B4';

const PX_PER_HOUR = 68;    // Hauteur d'une heure en pixels
const START_HOUR = 0;      // Grille affichée de 0h à 24h
const END_HOUR = 24;

// Convertit "HH:MM" en heure décimale (ex. "14:30" → 14.5)
const parseHHMM = (hhmm) => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h + m / 60;
};

const isPosted = (t) => (t.source === 'video_to_post' || t.source === 'asa_client_preview') && t.status === 'fini';

// Répartit les tâches qui se chevauchent en colonnes côte à côte (style Apple Calendar).
function layoutLanes(tasks) {
  const items = tasks
    .map((t) => {
      const start = parseHHMM(t.heure_debut);
      return { t, start, end: parseHHMM(t.heure_fin) || start + 1 };
    })
    .sort((a, b) => a.start - b.start || a.end - b.end);
  const out = new Map();
  let cluster = [];
  let clusterEnd = -1;
  const flush = () => {
    const lanes = [];
    cluster.forEach((it) => {
      let lane = lanes.findIndex((end) => end <= it.start);
      if (lane === -1) { lane = lanes.length; lanes.push(it.end); } else lanes[lane] = it.end;
      out.set(it.t.id, { lane });
    });
    cluster.forEach((it) => { out.get(it.t.id).lanes = lanes.length; });
    cluster = [];
  };
  items.forEach((it) => {
    if (cluster.length && it.start >= clusterEnd) flush();
    cluster.push(it);
    clusterEnd = Math.max(clusterEnd, it.end);
  });
  if (cluster.length) flush();
  return out;
}

const STATUS_COLORS = {
  a_faire: '#5BBEFC', en_cours: '#F5A623', a_valider: '#F5C46A',
  a_refaire: '#FF7B7B', fini: '#22D3B6',
};

export default function DayTimelineView({ day, tasks, onSelectDay, onOpenTask, onCreate, isMobile, onBackToMonth, onSwitchToKanban, reload, optimisticPatch }) {
  const containerRef = useRef(null);
  const gridRef = useRef(null);
  const scrollerRef = useRef(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const [draggedTask, setDraggedTask] = useState(null);

  // Scroll auto à 7h au mount (comme Apple Calendar)
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = (7 - START_HOUR) * PX_PER_HOUR - 20;
    }
  }, [day]);

  const dayTasks = useMemo(() => {
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return tasks.filter((t) => ((t.date_debut || '').slice(0, 10) <= day && (t.date_fin || '').slice(0, 10) >= day)
      || (day === today && !t.date_debut && !t.source_video_id && t.status !== 'fini'));
  }, [tasks, day]);

  // Tâches avec heure assignée → positionnées sur la timeline
  // Tâches sans heure → épinglées en haut dans une bande "sans heure"
  const timed = dayTasks.filter((t) => t.heure_debut);
  const untimed = dayTasks.filter((t) => !t.heure_debut);
  const lanes = useMemo(() => layoutLanes(timed), [timed]);

  const dayLabel = useMemo(() => {
    const d = new Date(day + 'T12:00:00');
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  }, [day]);

  const prevDay = () => {
    const d = new Date(day + 'T12:00:00'); d.setDate(d.getDate() - 1);
    onSelectDay(d.toISOString().slice(0, 10));
  };
  const nextDay = () => {
    const d = new Date(day + 'T12:00:00'); d.setDate(d.getDate() + 1);
    onSelectDay(d.toISOString().slice(0, 10));
  };

  // Marker "maintenant" si on affiche aujourd'hui
  const isToday = day === new Date().toISOString().slice(0, 10);
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    if (!isToday) return;
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, [isToday]);
  const nowOffset = isToday ? (now.getHours() + now.getMinutes() / 60 - START_HOUR) * PX_PER_HOUR : null;

  const handleGridClick = (e) => {
    // Clic sur la grille vide → quick-create avec l'heure sous le clic
    if (e.target !== e.currentTarget) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top + e.currentTarget.scrollTop;
    const hour = START_HOUR + y / PX_PER_HOUR;
    const h = Math.floor(hour);
    const m = Math.round((hour - h) * 4) * 15; // snap 15 min
    const hhmm = `${String(h).padStart(2, '0')}:${String(m === 60 ? 0 : m).padStart(2, '0')}`;
    onCreate?.({ day, heure_debut: hhmm });
  };

  // Iter 62fi — Drag & drop d'une tâche sur une nouvelle heure.
  // Snap à 15 min. Préserve la durée (heure_fin ajustée).
  // Iter 62fj — Fonctionne AUSSI pour les tâches "sans heure fixe" (chips du haut)
  // qui atterrissent sur la grille → acquièrent une heure_debut au drop.
  const handleDragEnd = async (e) => {
    const { active, delta } = e;
    setDraggedTask(null);
    const task = tasks.find((t) => t.id === active?.id);
    if (!task || !delta) return;
    const startH = parseHHMM(task.heure_debut);
    if (startH === null) {
      // Tâche "sans heure fixe" draggée → calcul via position absolue du drag
      // par rapport à la grille horaire (gridRef).
      const translated = active.rect?.current?.translated;
      const grid = gridRef.current;
      if (!translated || !grid) return;
      const gridRect = grid.getBoundingClientRect();
      const scrollTop = scrollerRef.current?.scrollTop || 0;
      // Position Y du centre du drag relative au haut de la grille (en px grille)
      const yInGrid = (translated.top + translated.height / 2) - gridRect.top + scrollTop;
      // Si drop en dehors de la grille → abort
      if (yInGrid < 0 || yInGrid > (END_HOUR - START_HOUR) * PX_PER_HOUR + 20) return;
      const newHour = START_HOUR + yInGrid / PX_PER_HOUR;
      await applyNewHour(task, newHour);
      return;
    }
    const dy = delta.y;
    if (Math.abs(dy) < 4) return; // click, pas drag
    const deltaHours = dy / PX_PER_HOUR;
    const newStart = Math.max(0, Math.min(23.99, startH + deltaHours));
    await applyNewHour(task, newStart);
  };

  const applyNewHour = async (task, decimalHour) => {
    // Snap à 15 min
    const totalMin = Math.round(decimalHour * 60 / 15) * 15;
    const h = Math.max(0, Math.min(23, Math.floor(totalMin / 60)));
    const m = totalMin % 60;
    const newDebut = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    // Préserve la durée si heure_fin existait
    let newFin = null;
    const oldStart = parseHHMM(task.heure_debut);
    const oldEnd = parseHHMM(task.heure_fin);
    if (oldStart !== null && oldEnd !== null) {
      const duration = oldEnd - oldStart; // heures décimales
      const endDecimal = h + m / 60 + duration;
      const eH = Math.max(0, Math.min(23, Math.floor(endDecimal)));
      const eM = Math.round((endDecimal - Math.floor(endDecimal)) * 60);
      newFin = `${String(eH).padStart(2, '0')}:${String(Math.min(eM, 59)).padStart(2, '0')}`;
    }
    const dated = task.date_debut ? {} : { date_debut: day, date_fin: day };
    optimisticPatch?.(task.id, { ...dated, heure_debut: newDebut, heure_fin: newFin || task.heure_fin });
    try {
      await api.patch(`/asa-tasks/${task.id}`, { ...dated, heure_debut: newDebut, heure_fin: newFin || task.heure_fin });
      toast.success(`Déplacée à ${newDebut}`);
      reload?.();
    } catch (err) {
      optimisticPatch?.(task.id, { heure_debut: task.heure_debut, heure_fin: task.heure_fin });
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div data-testid="day-timeline-view" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 180px)', minHeight: 500 }}>
    <DndContext sensors={sensors} onDragStart={(e) => setDraggedTask(tasks.find((t) => t.id === e.active.id))} onDragEnd={handleDragEnd}>
      {/* Header navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        {onBackToMonth && (
          <button
            onClick={onBackToMonth}
            data-testid="day-timeline-back-month"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 12px', borderRadius: 10,
              background: SURFACE2, border: `1px solid ${BORDER}`,
              color: T2, cursor: 'pointer',
              fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
              letterSpacing: '-0.005em',
            }}
            title="Retour à la vue mois"
          >
            <ChevronLeft size={14} /> Mois
          </button>
        )}
        <button
          onClick={prevDay}
          data-testid="day-timeline-prev"
          style={{ width: 36, height: 36, borderRadius: 10, background: SURFACE2, border: `1px solid ${BORDER}`, color: T1, cursor: 'pointer', display: 'grid', placeItems: 'center' }}
        ><ChevronLeft size={16} /></button>
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: isMobile ? 15 : 18, color: T1, textTransform: 'capitalize', letterSpacing: '-0.01em', flex: 1 }}>
          {dayLabel}
          {isToday && <span style={{ marginLeft: 10, padding: '2px 9px', borderRadius: 999, background: `${BLUE}22`, color: BLUE_LIGHT, fontSize: 11, fontWeight: 700 }}>Aujourd'hui</span>}
        </div>
        <button
          onClick={nextDay}
          data-testid="day-timeline-next"
          style={{ width: 36, height: 36, borderRadius: 10, background: SURFACE2, border: `1px solid ${BORDER}`, color: T1, cursor: 'pointer', display: 'grid', placeItems: 'center' }}
        ><ChevronRight size={16} /></button>
        {onSwitchToKanban && (
          <button
            onClick={onSwitchToKanban}
            data-testid="day-timeline-to-kanban"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 12px', borderRadius: 10,
              background: `${BLUE}18`, border: `1px solid ${BLUE}50`,
              color: BLUE_LIGHT, cursor: 'pointer',
              fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
              letterSpacing: '-0.005em',
            }}
            title="Basculer sur la vue Kanban de ce jour"
          >
            <LayoutGrid size={13} /> Kanban
          </button>
        )}
        <button
          onClick={() => onCreate?.({ day })}
          data-testid="day-timeline-new"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 16px', borderRadius: 12,
            background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE} 100%)`,
            border: 'none', color: NAVY,
            fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5, cursor: 'pointer',
            boxShadow: '0 8px 22px -6px rgba(62,123,250,0.55), inset 0 1px 0 rgba(255,255,255,0.28)',
          }}
        >
          <Plus size={14} strokeWidth={3} /> Nouvelle tâche
        </button>
      </div>

      {/* Bande "sans heure" — Iter 62fj : chips DRAGGABLES vers la grille horaire */}
      {untimed.length > 0 && (
        <div style={{ marginBottom: 10, padding: '10px 12px', borderRadius: 12, background: SURFACE, border: `1px solid ${BORDER}` }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 10, color: T3, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Sans heure fixe ({untimed.length}) · drag vers la grille pour fixer une heure
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {untimed.map((t) => (
              <UntimedChip key={t.id} task={t} onOpen={onOpenTask} />
            ))}
          </div>
        </div>
      )}

      {/* Grille horaire scrollable */}
      <div
        ref={scrollerRef}
        style={{ flex: 1, overflow: 'auto', background: SURFACE, borderRadius: 12, border: `1px solid ${BORDER}`, position: 'relative' }}
      >
        <div
          ref={gridRef}
          onClick={handleGridClick}
          data-timeline-grid="1"
          style={{ position: 'relative', height: (END_HOUR - START_HOUR) * PX_PER_HOUR, display: 'grid', gridTemplateColumns: '58px 1fr' }}
        >
          {/* Colonne heures */}
          <div style={{ position: 'relative', borderRight: `1px solid ${BORDER}`, pointerEvents: 'none' }}>
            {Array.from({ length: END_HOUR - START_HOUR }, (_, i) => {
              const h = START_HOUR + i;
              return (
                <div
                  key={h}
                  style={{
                    position: 'absolute', top: i * PX_PER_HOUR, right: 8,
                    fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5,
                    color: T3, fontWeight: 600,
                  }}
                >{String(h).padStart(2, '0')}:00</div>
              );
            })}
          </div>
          {/* Colonne tâches / grille */}
          <div style={{ position: 'relative' }}>
            {/* Lignes horaires */}
            {Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute', top: i * PX_PER_HOUR, left: 0, right: 0,
                  height: 1, background: 'rgba(50,64,120,0.25)',
                  pointerEvents: 'none',
                }}
              />
            ))}
            {/* Ligne demi-heure (subtile) */}
            {Array.from({ length: END_HOUR - START_HOUR }, (_, i) => (
              <div
                key={`h-${i}`}
                style={{
                  position: 'absolute', top: i * PX_PER_HOUR + PX_PER_HOUR / 2, left: 0, right: 0,
                  height: 1, background: 'rgba(50,64,120,0.12)',
                  pointerEvents: 'none',
                }}
              />
            ))}

            {/* Marqueur "maintenant" avec heure en pill */}
            {nowOffset !== null && (
              <>
                {/* Pill d'heure dans la gouttière gauche */}
                <div data-testid="now-marker-time" style={{
                  position: 'absolute', top: nowOffset - 11, left: -58, width: 50,
                  textAlign: 'right', paddingRight: 6,
                  fontFamily: 'JetBrains Mono, monospace', fontSize: 11, fontWeight: 800,
                  color: '#FF4D4D', zIndex: 4, pointerEvents: 'none',
                  textShadow: '0 0 10px rgba(255,77,77,0.5)',
                }}>
                  {String(now.getHours()).padStart(2, '0')}:{String(now.getMinutes()).padStart(2, '0')}
                </div>
                <div style={{
                  position: 'absolute', top: nowOffset, left: -4, right: 0,
                  height: 2, background: '#FF4D4D',
                  boxShadow: '0 0 8px rgba(255,77,77,0.6)', zIndex: 3, pointerEvents: 'none',
                }}>
                  <div style={{ position: 'absolute', left: -8, top: -4, width: 10, height: 10, borderRadius: '50%', background: '#FF4D4D' }} />
                </div>
              </>
            )}

            {/* Tâches positionnées (draggables, snap 15min) */}
            {timed.map((t) => {
              const startH = parseHHMM(t.heure_debut);
              const endH = parseHHMM(t.heure_fin) || (startH + 1);
              const top = (startH - START_HOUR) * PX_PER_HOUR;
              const height = Math.max((endH - startH) * PX_PER_HOUR - 2, 28);
              const color = STATUS_COLORS[t.status] || BLUE_LIGHT;
              const { lane = 0, lanes: n = 1 } = lanes.get(t.id) || {};
              return (
                <TimelineTaskTile
                  key={t.id}
                  task={t}
                  top={top}
                  height={height}
                  color={color}
                  lane={lane}
                  lanes={n}
                  onOpen={onOpenTask}
                />
              );
            })}
          </div>
        </div>
      </div>
      <div style={{ fontFamily: 'Sora', fontWeight: 500, fontSize: 10.5, color: T3, marginTop: 8, textAlign: 'center' }}>
        Clique sur une case vide pour créer une tâche · Drag les tâches pour changer leur heure
      </div>
      <DragOverlay>
        {draggedTask && (
          <div style={{
            padding: '8px 12px', borderRadius: 9,
            background: `linear-gradient(180deg, ${STATUS_COLORS[draggedTask.status] || BLUE_LIGHT}44 0%, ${STATUS_COLORS[draggedTask.status] || BLUE_LIGHT}26 100%)`,
            border: `1px solid ${STATUS_COLORS[draggedTask.status] || BLUE_LIGHT}cc`,
            borderLeft: `3px solid ${STATUS_COLORS[draggedTask.status] || BLUE_LIGHT}`,
            color: T1, fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
            boxShadow: '0 20px 40px -8px rgba(0,0,0,0.5)',
            cursor: 'grabbing',
          }}>{draggedTask.title}</div>
        )}
      </DragOverlay>
    </DndContext>
    </div>
  );
}

// Iter 62fj — Chip "sans heure fixe" draggable vers la grille horaire.
function UntimedChip({ task, onOpen }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: task.id });
  const color = STATUS_COLORS[task.status] || BLUE_LIGHT;
  return (
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={(e) => { e.stopPropagation(); if (!isDragging) onOpen(task); }}
      data-testid={`timeline-untimed-${task.id}`}
      style={{
        padding: '5px 10px', borderRadius: 8,
        background: `${color}22`,
        border: `1px solid ${color}66`,
        color, cursor: isDragging ? 'grabbing' : 'grab',
        fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
        maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        opacity: isDragging ? 0.3 : (isPosted(task) ? 0.45 : 1),
        filter: isPosted(task) ? 'grayscale(0.85)' : 'none',
        textDecoration: isPosted(task) ? 'line-through' : 'none',
        transition: isDragging ? 'none' : 'all 0.12s var(--ease-out)',
        zIndex: isDragging ? 20 : 1,
      }}
    >{task.title}</button>
  );
}

// Iter 62fi — Tile de tâche draggable sur la timeline (snap 15min).
function TimelineTaskTile({ task, top, height, color, lane = 0, lanes = 1, onOpen }) {
  const posted = isPosted(task);
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: task.id });
  return (
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-testid={`timeline-task-${task.id}`}
      onClick={(e) => { e.stopPropagation(); if (!isDragging) onOpen(task); }}
      style={{
        position: 'absolute', top, height,
        left: `calc(6px + (100% - 12px) * ${lane / lanes})`,
        width: `calc((100% - 12px) / ${lanes} - ${lanes > 1 ? 3 : 0}px)`,
        padding: '6px 9px', borderRadius: 9,
        background: `linear-gradient(180deg, ${color}2E 0%, ${color}1A 100%)`,
        border: `1px solid ${color}80`,
        borderLeft: `3px solid ${color}`,
        color: '#F0F4FF', cursor: isDragging ? 'grabbing' : 'grab',
        textAlign: 'left', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        boxShadow: `0 4px 14px -4px ${color}66`,
        opacity: isDragging ? 0.3 : (posted ? 0.45 : 1),
        filter: posted ? 'grayscale(0.85)' : 'none',
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition: isDragging ? 'none' : 'box-shadow 0.12s var(--ease-out)',
        zIndex: isDragging ? 10 : 1,
      }}
    >
      <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 12, lineHeight: 1.25, letterSpacing: '-0.005em', color: '#F0F4FF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: height < 40 ? 'nowrap' : 'normal' }}>{task.title}</div>
      {(height >= 44 || task.source === 'gcal') && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color, marginTop: 2, fontWeight: 700 }}>
          <span data-testid={`timeline-slot-${task.id}`}>{task.heure_debut}{task.heure_fin ? ` → ${task.heure_fin}` : ''}</span>
          <JoinCallButton task={task} compact />
        </div>
      )}
    </button>
  );
}
